import { useEffect, useState } from "react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Trophy, Rocket, Check, Loader2, Lock, Zap } from "lucide-react";

const CAP = 100;

export default function Founder() {
  const [stats, setStats] = useState({ claimed: 0, remaining: CAP });
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    api.get("/founder/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const claim = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/founder/reserve", { email });
      toast.success("Spot reserved! Check your email for payment details.");
      setJoined(true);
      const r = await api.get("/founder/stats"); setStats(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't reserve — try again");
    } finally { setBusy(false); }
  };

  const pct = Math.min(100, Math.round((stats.claimed / CAP) * 100));

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="founder-page">
      {/* Hero */}
      <div className="text-center pt-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--gain))]/15 border border-[hsl(var(--gain))]/30 text-xs uppercase tracking-widest font-bold text-[hsl(var(--gain))] mb-4">
          <Trophy className="w-3.5 h-3.5" /> Limited to 100 founders
        </div>
        <h1 className="font-display text-4xl md:text-6xl font-black">
          Aanar Plus <span className="text-[hsl(var(--gain))]">for life</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mt-4 max-w-xl mx-auto">
          Pay <span className="text-foreground font-mono-tab font-bold">$299 once</span>. Never pay another month. Grandfathered into every future Plus feature.
        </p>
      </div>

      {/* Live counter */}
      <Card className="p-6 rounded-3xl border-2 border-[hsl(var(--gain))]/40" data-testid="founder-counter">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-muted-foreground">Founder spots claimed</span>
          <span className="font-mono-tab text-lg font-bold" data-testid="founder-count">
            {stats.claimed} <span className="text-muted-foreground">of {CAP}</span>
          </span>
        </div>
        <div className="h-3 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[hsl(var(--gain))] to-[hsl(var(--ai))] transition-all"
               style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {stats.remaining > 0 ? `${stats.remaining} spots left` : "Waitlist only — sold out"}
        </div>
      </Card>

      {/* Value stack */}
      <Card className="p-6 rounded-3xl" data-testid="founder-value">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">What you get</div>
        <ul className="space-y-2.5">
          {[
            "Aanar Plus, forever — no monthly bill, ever",
            "All future Plus features included, no additional cost",
            "Founding member badge on your public profile",
            "Direct Slack channel with the Aanar team",
            "First access to every new bot template + integration",
            "Priority IBKR / Alpaca / Tradier support",
            "5 free bot template unlocks",
            "Vote on the roadmap — Founders decide what Aanar builds next",
          ].map((line, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm">
              <Check className="w-4 h-4 text-[hsl(var(--gain))] mt-0.5 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <div className="mt-5 pt-4 border-t border-border text-sm">
          <div className="flex items-baseline justify-between">
            <span>Monthly Plus over 3 years</span>
            <span className="font-mono-tab text-muted-foreground line-through">$504</span>
          </div>
          <div className="flex items-baseline justify-between mt-1">
            <span className="font-semibold">Founder Lifetime</span>
            <span className="font-mono-tab text-2xl font-black text-[hsl(var(--gain))]">$299</span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Pays for itself in 22 months. Then it's free forever.</div>
        </div>
      </Card>

      {/* Claim form */}
      {joined ? (
        <Card className="p-6 rounded-3xl text-center bg-[hsl(var(--gain))]/10 border-[hsl(var(--gain))]/40" data-testid="founder-joined">
          <Check className="w-10 h-10 mx-auto text-[hsl(var(--gain))] mb-2" />
          <div className="font-display text-xl font-bold">You're on the list!</div>
          <p className="text-sm text-muted-foreground mt-1">
            Check your email in the next 5 minutes for payment instructions. Reply to that email if you have any questions — the team reads every one.
          </p>
        </Card>
      ) : stats.remaining > 0 ? (
        <Card className="p-6 rounded-3xl" data-testid="founder-form">
          <div className="font-display text-lg font-semibold mb-3">Claim your spot</div>
          <form onSubmit={claim} className="space-y-3">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="founder-email"
              className="rounded-full h-12"
            />
            <Button type="submit" disabled={busy || !email}
                    data-testid="founder-submit"
                    size="lg" className="w-full rounded-full h-12 bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-background font-bold">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
              Reserve my Founder spot · $299
            </Button>
            <div className="flex items-center gap-1.5 justify-center text-xs text-muted-foreground">
              <Lock className="w-3 h-3" /> Payment via Stripe · 30-day money-back guarantee
            </div>
          </form>
        </Card>
      ) : (
        <Card className="p-6 rounded-3xl text-center" data-testid="founder-soldout">
          <div className="font-display text-lg font-bold">Founder spots sold out</div>
          <p className="text-sm text-muted-foreground mt-1">
            Join the Plus waitlist and we'll notify you the moment monthly signups open.
          </p>
        </Card>
      )}

      {/* Trust footer */}
      <div className="text-center text-xs text-muted-foreground pb-6">
        30-day money-back guarantee · Not financial advice · aanar.app
      </div>
    </div>
  );
}
